import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SubmittedRequests.css';
import { useOpportunityAnalyticsContext } from '..';
import { getAccessToken } from 'src/utils/token-service';
import { useMsal } from '@azure/msal-react';

interface Request {
	id: number;
	status: string;
	dropdown1: string;
	dropdown2: string;
	startDate: string;
	description: string;
	endDate: string;
	userId: string;
}

const CARDS_PER_ROW = 3;
const PAGE_SIZE = CARDS_PER_ROW * 2;

const SubmittedRequests: React.FC = () => {
	const { requests, setRequests } = useOpportunityAnalyticsContext();
	const { instance } = useMsal();

	const [currentPage, setCurrentPage] = useState(1);
	const [paginatedRequests, setPaginatedRequests] = useState<Request[]>([]);
	const [isTableView, setIsTableView] = useState(true);
	const [isLoading, setIsLoading] = useState(false); // Track loading state

	useEffect(() => {
		const fetchRequests = async () => {
			setIsLoading(true); // Start loading
			const accessToken = await getAccessToken();

			try {
				const accounts = instance.getAllAccounts();
				const loggedInUserName = accounts && accounts.length > 0 ? accounts[0].username : '';

				// Fetch requests for flag=1
				const apiUrlFlag1 = `https://api-dv.brightspeed.com/genai-studio/get_request_status/?userid=${loggedInUserName}&flag=1`;
				const responseFlag1 = await fetch(apiUrlFlag1, {
					headers: { Authorization: `Bearer ${accessToken}` },
				});
				const dataFlag1 = await responseFlag1.json();

				const transformedRequestsFlag1 = Object.keys(dataFlag1.id).map((key) => ({
					id: dataFlag1.id[key],
					status: dataFlag1.status[key],
					dropdown1: dataFlag1.dropdown1[key],
					dropdown2: dataFlag1.dropdown2[key],
					startDate: dataFlag1.start_date[key],
					endDate: dataFlag1.end_date[key],
					description: dataFlag1.description[key],
					userId: dataFlag1.userid[key],
				}));

				const apiUrlFlag0 = `https://api-dv.brightspeed.com/genai-studio/get_request_status/?userid=${loggedInUserName}&flag=0`;
				const responseFlag0 = await fetch(apiUrlFlag0, {
					headers: { Authorization: `Bearer ${accessToken}` },
				});
				const dataFlag0 = await responseFlag0.json();

				const transformedRequestsFlag0 = Object.keys(dataFlag0.id).map((key) => ({
					id: dataFlag0.id[key],
					status: dataFlag0.status[key],
					dropdown1: dataFlag0.dropdown1[key],
					dropdown2: dataFlag0.dropdown2[key],
					startDate: dataFlag0.start_date[key],
					endDate: dataFlag0.end_date[key],
					description: dataFlag0.description[key],
					userId: dataFlag0.userid[key],
				}));

				// Separate logged-in user's requests and others
				const loggedInUserRequests = [...transformedRequestsFlag1, ...transformedRequestsFlag0].filter(
					(request) => request.userId === loggedInUserName
				);

				const otherUserRequests = [...transformedRequestsFlag1, ...transformedRequestsFlag0].filter(
					(request) => request.userId !== loggedInUserName
				);

				const allRequests = [...loggedInUserRequests, ...otherUserRequests];
				setRequests(allRequests);
			} catch (error) {
				console.error('Error fetching requests:', error);
			} finally {
				setIsLoading(false); // Stop loading once data is fetched
			}
		};

		fetchRequests();
	}, [setRequests, instance]);

	useEffect(() => {
		const offset = (currentPage - 1) * PAGE_SIZE;
		const currentPageRequests = requests.slice(offset, offset + PAGE_SIZE);
		setPaginatedRequests(currentPageRequests);
	}, [requests, currentPage]);

	const navigate = useNavigate();

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const totalPages = Math.ceil(requests.length / PAGE_SIZE);

	return (
		<div className="submitted-requests-container">
			<div className="page-description">
				<h2 className="submitted-request">Submitted Requests</h2>
				<p className="text">This page displays requests submitted by you as well as by other users</p>
			</div>
			<div className="view-toggle-container">
				<button className="view-toggle-btn" onClick={() => setIsTableView(!isTableView)}>
					Switch to {isTableView ? 'Card' : 'Table'} View
				</button>
			</div>

			{/* Show loading spinner when isLoading is true */}
			{isLoading ? (
				<div className="loading-spinner">{/* <span>Loading...</span> */}</div>
			) : requests.length === 0 ? (
				<div className="alert">You don't have any submitted requests</div>
			) : isTableView ? (
				<>
					<table className="requests-table">
						<thead>
							<tr>
								<th>Request ID</th>
								<th>Status</th>
								<th>Parent Intent</th>
								<th>Child Intent</th>
								<th>Start Date</th>
								<th>End Date</th>
								<th>Description</th>
								<th>User ID</th>
								<th>Action</th>
							</tr>
						</thead>
						<tbody>
							{paginatedRequests.map((request) => (
								<tr key={request.id}>
									<td>{request.id}</td>
									<td>{request.status}</td>
									<td>{request.dropdown1}</td>
									<td>{request.dropdown2}</td>
									<td>{request.startDate}</td>
									<td>{request.endDate}</td>
									<td>{request.description}</td>
									<td>{request.userId}</td>
									<td>
										{request.status === 'Completed' && (
											<button className="action-button" onClick={() => navigate(`/opportunity-analytics`)}>
												View Result
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>

					<div className="pagination-container">
						<button
							className="pagination-btn"
							onClick={() => handlePageChange(currentPage - 1)}
							disabled={currentPage === 1}
						>
							Previous
						</button>
						<span className="pagination-info">
							Page {currentPage} of {totalPages}
						</span>
						<button
							className="pagination-btn"
							onClick={() => handlePageChange(currentPage + 1)}
							disabled={currentPage === totalPages}
						>
							Next
						</button>
					</div>
				</>
			) : (
				<>
					<div className="request-cards-container">
						{paginatedRequests.map((request) => (
							<div key={request.id} className="request-card">
								<h5 className="card-title">Request ID: {request.id}</h5>
								<div className="card-details">
									<p>
										<strong>Status:</strong> {request.status}
									</p>
									<p>
										<strong>Parent Intent:</strong> {request.dropdown1}
									</p>
									<p>
										<strong>Child Intent:</strong> {request.dropdown2}
									</p>
									<p>
										<strong>Start Date:</strong> {request.startDate}
									</p>
									<p>
										<strong>End Date:</strong> {request.endDate}
									</p>
									<p>
										<strong>User ID:</strong> {request.userId}
									</p>
								</div>
								{request.status === 'Completed' && (
									<button className="card-action-btn" onClick={() => navigate(`/opportunity-analytics`)}>
										View Result
									</button>
								)}
							</div>
						))}
					</div>

					<div className="pagination-container">
						<button
							className="pagination-btn"
							onClick={() => handlePageChange(currentPage - 1)}
							disabled={currentPage === 1}
						>
							Previous
						</button>
						<span className="pagination-info">
							Page {currentPage} of {totalPages}
						</span>
						<button
							className="pagination-btn"
							onClick={() => handlePageChange(currentPage + 1)}
							disabled={currentPage === totalPages}
						>
							Next
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export default SubmittedRequests;
